import { useState } from "react";
import { getTime, getStatusColor } from "../helpers";
import {
  getPublishingProductsByCampaignId,
  updatePublishProduct,
  deletePublishingProduct,
  getProofImagesByDesignRequestId,
  getGalleryImagesByDesignRequestId,
  getCatalogProductBySku,
  getOrganization,
  createLicense,
  getMember,
} from "../../../../services/axios";
import { useHistory, Link } from "react-router-dom";
import { LicenseType, PublishProduct } from "../../../../utils/types";
import { toast } from "sonner";
import { groupBy } from "../../../../utils";

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export function ProductsTable({
  licensing,
  publishedProducts,
  cid,
  page,
  previousPage,
  nextPage,
}: {
  licensing: any[];
  publishedProducts: PublishProduct[];
  cid: string;
  page: number;
  previousPage: () => void;
  nextPage: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  let history = useHistory();

  if (!publishedProducts) {
    return <></>;
  }

  function retrieveLicensingStatusForProduct(productId: string) {
    const filtered = licensing.filter((x) =>
      x.product_ids?.includes(productId)
    );
    if (filtered.length > 0) {
      return filtered[0];
    } else {
      return null;
    }
  }

  async function publishThisProduct(product: PublishProduct) {
    return updatePublishProduct({
      ...product,
      status: "start",
      updated_at: new Date().getTime().toString(),
    })
      .then(() => {
        toast.success(`started!`, { duration: 5000 });
      })
      .catch((e) => {
        toast.error(e.message, { duration: 5000 });
      });
  }

  async function deleteThisProduct(productId: string) {
    return deletePublishingProduct(productId)
      .then(() => {
        toast.success(`deleted`, { duration: 5000 });
      })
      .catch((e) => {
        toast.error(e.message, { duration: 5000 });
      });
  }

  function reviewProduct(productId: string) {
    history.push({
      pathname: `/products/${productId}`,
    });
  }

  async function publishProducts() {
    let request = await getPublishingProductsByCampaignId(cid);
    let products = request.items;
    if (!products) {
      return;
    }
    for (let i = 0; i < products.length; i++) {
      let p = products[i];
      if (p.status === "pending products") {
        continue;
      }
      if (p.status !== "review") {
        continue;
      }
      await updatePublishProduct({
        ...p,
        status: "start",
        updated_at: new Date().getTime().toString(),
      })
        .then(() => toast.success(`started!`, { duration: 5000 }))
        .catch((e) => toast.error(e.message, { duration: 5000 }));
    }
  }

  async function publishLicensing() {
    const products = publishedProducts.filter((x) => selected.includes(x.id));
    let affinityProducts = products.filter((x) =>
      x.licensing.includes("affinity")
    );
    let clCProducts = products.filter((x) =>
      x.licensing.includes("nonaffinity")
    );
    await postAffinity(affinityProducts);
    await postClC(clCProducts);
    return;
  }

  async function postClC(clcProducts: PublishProduct[]) {
    if (!clcProducts.length) {
      return;
    }
    const groupedBySku = groupBy(clcProducts, "sku");
    const keys = Object.keys(groupedBySku);
    for (let i = 0; i < keys.length; i++) {
      const sku = keys[i];
      const athletes = [];

      const catalogProduct = await getCatalogProductBySku(sku);
      if (!catalogProduct) {
        toast.error(`missing catalog product ${sku}`, {
          duration: 5000,
        });
        continue;
      }

      const productByKey = groupedBySku[sku] as PublishProduct[];

      const athleteIds = [
        ...new Set(productByKey.map((x) => x.athletes).flat()),
      ];
      if (!athleteIds.length) {
        toast.error(`we are expecting at least 1 athlete per product.`, {
          duration: 5000,
        });
        return;
      }
      for (let j = 0; j < athleteIds.length; j++) {
        const athleteId = athleteIds[j];
        const athlete = await getMember(athleteId);
        athletes.push(athlete);
      }
      const organizationIds = [
        ...new Set(productByKey.map((x) => x.organizations).flat()),
      ];
      if (!organizationIds.length) {
        toast.error(`we are expecting at least 1 organization per product.`, {
          duration: 5000,
        });
        return;
      }
      if (organizationIds.length > 1) {
        toast.error(
          `we are expecting no more than 1 organization per product.`,
          {
            duration: 5000,
          }
        );
        return;
      }
      const organizationId = organizationIds[0];
      const organization = await getOrganization(organizationId);

      if (!organization) {
        toast.error(`missing organization.`, {
          duration: 5000,
        });
        return;
      }

      const originalFiles = [
        ...new Set(productByKey.map((x) => x.original_files).flat()),
      ];
      if (originalFiles.length > 2) {
        toast.error(`we are expecting no more than 2 designs per product.`, {
          duration: 5000,
        });
        return;
      }

      const designRequestIds = [
        ...new Set(productByKey.map((x) => x.design_templates).flat()),
      ];

      let designGalleryImages = [];
      let designProofImages = [];

      for (let j = 0; j < designRequestIds.length; j++) {
        const designRequestId = designRequestIds[j];
        const galleries = await getGalleryImagesByDesignRequestId(
          designRequestId
        );
        for (let p = 0; p < galleries.length; p++) {
          const gallery = galleries[p].file;
          designGalleryImages.push(gallery);
        }
        const proofs = await getProofImagesByDesignRequestId(designRequestId);
        for (let p = 0; p < proofs.length; p++) {
          const proof = proofs[p].file;
          designProofImages.push(proof);
        }
      }
      if (productByKey.length === 0) {
        toast.error(`we are missing products`, {
          duration: 5000,
        });
        return;
      }
      const productToLicense = productByKey[0];
      const product_gament_color_names = productToLicense.colors;
      const licenseRequest: Omit<
        LicenseType,
        "id" | "pk" | "affinity_status_id" | "created_at" | "updated_at"
      > = {
        affinity_internal_code: cid,
        affinity_license_id: "",
        affinity_organization_id: "",
        affinity_product_category_id: "",
        affinity_upi: [],
        author: productToLicense.author,
        campaign: productToLicense.campaign,
        description: productToLicense.metafields_global_description_tag,
        design_colors: [],
        design_gallery_images: designGalleryImages,
        design_name: productToLicense.title,
        design_proof_images: designProofImages,
        design_templates: productToLicense.design_templates,
        isExpedited: false,
        isFunded: false,
        license_files: [],
        license_use: "Internal Consumption",
        licensor: "nonaffinity",
        athletes: athleteIds,
        organization_contact_email: "",
        organization_contact_name: "",
        organization_contact_phone: "",
        organization_id: organizationId,
        organization_name: organization.account_name,
        organizations: organizationIds,
        original_files: productToLicense.original_files,
        product_brand: catalogProduct.model,
        product_garmet_colors: product_gament_color_names,
        product_ids: [productToLicense.id],
        product_model: catalogProduct.model,
        product_quantity: "TBD",
        product_sku: catalogProduct.sku,
        status: "review",
        organizations_table: "",
        athletes_table: "",
      };
      console.log("licenseRequest", licenseRequest);
      const request_id = await createLicense(licenseRequest);
      if (!request_id) {
        toast.error("something went wrong", { duration: 5000 });
        continue;
      } else {
        toast.success(`License ${request_id} Created!`, { duration: 5000 });
      }
    }
  }

  async function postAffinity(affinityProducts: PublishProduct[]) {
    if (!affinityProducts.length) {
      return;
    }
    for (let i = 0; i < affinityProducts.length; i++) {
      const productToLicense = affinityProducts[i];
      const organizationIds = productToLicense.organizations;

      let designGalleryImages = [];
      let designProofImages = [];

      const designRequests = productToLicense.design_templates;

      for (let j = 0; j < designRequests.length; j++) {
        const designRequestId = designRequests[j];
        const proofs = await getProofImagesByDesignRequestId(designRequestId);
        for (let p = 0; p < proofs.length; p++) {
          const proof = proofs[p].file;
          designProofImages.push(proof);
        }
        const galleries = await getGalleryImagesByDesignRequestId(
          designRequestId
        );
        for (let p = 0; p < galleries.length; p++) {
          const gallery = galleries[p].file;
          designGalleryImages.push(gallery);
        }
      }

      for (let j = 0; j < organizationIds.length; j++) {
        const organizationId = organizationIds[j];
        const organization = await getOrganization(organizationId);
        if (!organization) {
          toast.error(`missing organization ${organizationId}`, {
            duration: 5000,
          });
          continue;
        }

        const sku = productToLicense.sku;
        const catalogProduct = await getCatalogProductBySku(sku);
        if (!catalogProduct) {
          toast.error(`missing catalog product ${sku}`, {
            duration: 5000,
          });
          continue;
        }

        const upi = productToLicense.metafields.find(
          (x) => x.key === "upi" && x.namespace === "affinity"
        )?.value;

        if (!upi) {
          toast.error(`missing upi on product ${productToLicense.id}`, {
            duration: 5000,
          });
          continue;
        }

        const product_gament_color_names = productToLicense.colors;
        const licenseRequest: Omit<
          LicenseType,
          "id" | "pk" | "affinity_status_id" | "created_at" | "updated_at"
        > = {
          affinity_internal_code: cid,
          affinity_license_id: "",
          affinity_organization_id: organization.affinity_organization_id,
          affinity_product_category_id:
            productToLicense.affinity_product_category_id,
          affinity_upi: [upi],
          author: productToLicense.author,
          campaign: productToLicense.campaign,
          description: productToLicense.metafields_global_description_tag,
          design_colors: [],
          design_gallery_images: designGalleryImages,
          design_name: productToLicense.title,
          design_proof_images: designProofImages,
          design_templates: productToLicense.design_templates,
          isExpedited: false,
          isFunded: false,
          license_files: [],
          license_use: "Internal Consumption",
          licensor: "affinity",
          athletes: [],
          organization_contact_email: "",
          organization_contact_name: "",
          organization_contact_phone: "",
          organization_id: organizationId,
          organization_name: organization.account_name,
          organizations: organizationIds,
          original_files: productToLicense.original_files,
          product_brand: catalogProduct.model,
          product_garmet_colors: product_gament_color_names,
          product_ids: [productToLicense.id],
          product_model: catalogProduct.model,
          product_quantity: "TBD",
          product_sku: catalogProduct.sku,
          status: "review",
          organizations_table: "",
          athletes_table: "",
        };
        const request_id = await createLicense(licenseRequest);
        if (!request_id) {
          toast.error("something went wrong", { duration: 5000 });
          continue;
        } else {
          toast.success(`License ${request_id} Created!`, { duration: 5000 });
        }
      }
    }
  }

  function handleSelect(id: string) {
    if (selected.includes(id)) {
      const removed = selected.filter((x) => x !== id);
      setSelected(removed);
    } else {
      setSelected([...selected, id]);
    }
  }

  async function deleteAllSelectedProducts() {
    for (let i = 0; i < selected.length; i++) {
      await deleteThisProduct(selected[i]);
      toast.success(selected[i], { duration: 5000 });
    }
    setSelected([]);
    toast.success("deleted", { duration: 5000 });
  }

  return (
    <div className="w-full p-8 m-4 border rounded">
      <div className="flex-1 min-w-0 w-full pb-2">
        <h4 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          Product Listing History
        </h4>
        <p className="italic text-xs">Limited to the last month of requests</p>
      </div>
      <div className="w-full flex justify-between items-center gap-4">
        <div className="space-x-4">
          <button
            onClick={() => setSelected(publishedProducts.map((x) => x.id))}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            select all
          </button>
          <button
            onClick={() => setSelected([])}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            de-select all
          </button>
        </div>
        <button
          onClick={() => deleteAllSelectedProducts()}
          className="bg-black px-4 py-2 text-white my-4"
        >
          delete selected
        </button>
      </div>
      <div>
        <table className="table-fixed w-full border-collapse border border-deeppurple text-xs">
          <thead>
            <tr>
              <th className="border border-deeppurple">select</th>
              <th className="border border-deeppurple">product id</th>
              <th className="border border-deeppurple">updated</th>
              <th className="border border-deeppurple">status</th>
              <th className="border border-deeppurple">athlete</th>
              <th className="border border-deeppurple">licensors</th>
              <th className="border border-deeppurple">license status</th>
              <th className="border border-deeppurple">title</th>
              <th className="border border-deeppurple">Brand - Sku</th>
              <th className="border border-deeppurple"></th>
              <th className="border border-deeppurple"></th>
            </tr>
          </thead>
          <tbody>
            {publishedProducts.map((product, index) => {
              const license = retrieveLicensingStatusForProduct(product.id);
              return (
                <tr className="p-2 m-2 group" key={index}>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    <div className="w-full h-full flex justify-center items-center">
                      <input
                        id={`${product.id}`}
                        name={`${product.id}`}
                        type="checkbox"
                        checked={selected.includes(`${product.id}`)}
                        className="focus:ring-dustyrose h-4 w-4 text-dustyrose border-gray-300 rounded"
                        onChange={() => handleSelect(`${product.id}`)}
                      />
                    </div>
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.id}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {getTime(product.updated_at)}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.status}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {JSON.stringify(product.athletes.join(","))}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.licensing.join(",")}
                  </td>
                  <td
                    className={classNames(
                      getStatusColor(license),
                      "p-2 border border-deeppurple truncate group-hover:bg-gray-50 has-tooltip"
                    )}
                  >
                    {license
                      ? license.status
                        ? license.status
                        : "none"
                      : "none"}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50 has-tooltip">
                    <span className="tooltip rounded shadow-lg p-2 bg-white border-2 text-ghPurple">
                      {product.title}
                    </span>
                    {product.title}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.variants && product.variants.length > 0 && (
                      <div>
                        {product.variants[0]?.brand}{" "}
                        {product.variants[0]?.model} {product.variants[0]?.sku}
                      </div>
                    )}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.status === "review" ? (
                      <button
                        className="underline"
                        onClick={() => publishThisProduct(product)}
                      >
                        Publish
                      </button>
                    ) : (
                      <Link
                        to={{
                          pathname: product.shopify_admin_url,
                        }}
                        target="_blank"
                      >
                        {product.shopify_admin_url}
                      </Link>
                    )}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.status === "review" ||
                    product.status === "pending products" ? (
                      <button
                        className="underline"
                        onClick={() => deleteThisProduct(product.id)}
                      >
                        Delete
                      </button>
                    ) : (
                      <Link
                        to={{
                          pathname: product.shopify_store_url,
                        }}
                        target="_blank"
                      >
                        {product.shopify_store_url}
                      </Link>
                    )}
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    {product.status === "review" ||
                    product.status === "pending products" ? (
                      <div>
                        <button
                          onClick={() => reviewProduct(product.id)}
                          className="underline"
                        >
                          Review
                        </button>
                      </div>
                    ) : (
                      <div>
                        <button
                          onClick={() => reviewProduct(product.id)}
                          className="underline"
                        >
                          View
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <nav
        className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Showing page <span className="font-medium">{`${page}`}</span>
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            onClick={() => previousPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Next
          </button>
        </div>
      </nav>
      <div className="w-full flex justify-start">
        <div className="my-2 ml-2">
          <button
            onClick={() => publishLicensing()}
            className="bg-black px-4 py-2 text-white my-4"
          >
            Generate Selected Licensing
          </button>
        </div>
        <div className="my-2 ml-2">
          <button
            onClick={() => publishProducts()}
            className="bg-black px-4 py-2 text-white my-4"
          >
            Publish Selected Products
          </button>
        </div>
      </div>
    </div>
  );
}
